/**
 * Performance Promo Code Form — AJAX submit + hydration.
 *
 * The form's server-rendered markup is promo-independent (that's the whole point of SP-612), so
 * everything the visitor sees about their active code is applied here: on load from the payload
 * the block fetched, and on submit from the POST response.
 *
 * Travels as a handle plus a namespace entry, like the other shared performance scripts — see the
 * header of performance-promo.js for why a cross-entry import doesn't work.
 */

const { hooks } = window.sitchco;
const { promo: promoState, hooks: HOOKS } = window.sitchco.performance;

const GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';
const EMPTY_ERROR = 'Please enter a promotional code.';

/**
 * Forms with a request in flight. Our own guard rather than a read of aria-busy, because
 * busy-submit.js may have set that attribute earlier in the same submit dispatch and listener
 * order between the two scripts isn't guaranteed.
 */
const pending = new WeakSet();

/** The promo each container last rendered as active — what a failed removal has to put back. */
const activePromo = new WeakMap();

function parts(container) {
    const form = container.querySelector('[data-promo-form]');
    return {
        form,
        input: form?.querySelector('input[name="promo_code"]'),
        button: form?.querySelector('[data-promo-submit]'),
        message: container.querySelector('[data-promo-message]'),
    };
}

/**
 * The promo code and its message come off the Criterion API unescaped — Twig used to escape them
 * on output. Everything here writes through textContent or value, never innerHTML.
 */
function showMessage(message, text, modifier) {
    if (!message) {
        return;
    }

    message.classList.remove('performance-promo-code-message--success', 'performance-promo-code-message--error');

    if (!text) {
        message.textContent = '';
        message.hidden = true;
        return;
    }

    message.classList.add(`performance-promo-code-message--${modifier}`);
    // Unhidden before it is written: this is a role="status" live region, and a mutation inside a
    // hidden subtree isn't announced — un-hiding afterwards doesn't replay it.
    message.hidden = false;
    message.textContent = text;
}

/**
 * Active state: the code is applied, so the control becomes "Remove".
 *
 * `error` re-renders the same active state with a failure notice instead of the promo's own
 * message — the state a rejected *removal* has to land in, since the code is still live.
 */
function showActive(container, promo, { error = '' } = {}) {
    const { form, input, button, message } = parts(container);
    if (!form) {
        return;
    }

    form.dataset.promoActive = '1';
    activePromo.set(container, promo);

    if (input) {
        input.value = promo.code ?? '';
        input.readOnly = true;
        // The code isn't invalid — a request failed. aria-invalid would say the wrong thing.
        input.removeAttribute('aria-invalid');
    }
    if (button) {
        button.textContent = 'Remove';
    }

    showMessage(message, error || (promo.message ?? ''), error ? 'error' : 'success');
}

/** Idle state: no code applied. `error` surfaces a rejected submission alongside the empty form. */
function showIdle(container, { keepValue = false, error = '' } = {}) {
    const { form, input, button, message } = parts(container);
    if (!form) {
        return;
    }

    delete form.dataset.promoActive;
    activePromo.delete(container);

    if (input) {
        input.readOnly = false;

        if (!keepValue) {
            input.value = '';
        }

        input.toggleAttribute('aria-invalid', Boolean(error));
    }
    if (button) {
        button.textContent = 'Submit';
    }

    showMessage(message, error, 'error');
}

/**
 * Apply the promo state the block just fetched.
 *
 * promoInvalid means the code we sent no longer resolves — expired, or forged. Clear it so the next
 * load is clean and say so once. Deliberately no re-fetch: the response already carries the
 * un-promoed listing, and firing PROMO_CHANGE from a DATA_LOAD handler would loop.
 */
function hydrate(container, { promo, promoInvalid, promoCode = null }) {
    if (promo) {
        showActive(container, promo);
        return;
    }
    if (promoInvalid) {
        // Passed on so clearCode() can tell a verdict about the cookie from a verdict about a
        // `?promo_code=` trigger that never resolved.
        promoState.clearCode(promoCode);
        showIdle(container, { error: promoState.restConfig().invalidMessage || GENERIC_ERROR });
        return;
    }

    showIdle(container);
}

async function handleSubmit(event, container) {
    const { form, input, button } = parts(container);
    event.preventDefault();

    if (pending.has(form)) {
        return;
    }

    const removing = Boolean(form.dataset.promoActive);
    const code = removing ? '' : (input?.value ?? '').trim();
    if (!removing && !code) {
        showIdle(container, {
            keepValue: true,
            error: EMPTY_ERROR,
        });

        input?.focus();
        return;
    }

    pending.add(form);
    // busy-submit.js sets this too when it happens to run first; setting it again is idempotent,
    // and clearing it is the part that script never does — it assumes a native navigation.
    button?.setAttribute('aria-busy', 'true');

    try {
        const result = await promoState.submit(code);
        // A sibling form's submission landed after this one and now owns the page state. Rendering
        // this response would contradict it; the winner's own PROMO_CHANGE has already synced every
        // form, so there is nothing left for the loser to do.
        if (result.superseded) {
            return;
        }
        if (result.valid && result.promo) {
            showActive(container, result.promo);
        } else if (result.cleared) {
            showIdle(container);
        } else {
            showIdle(container, {
                keepValue: true,
                error: result.message || GENERIC_ERROR,
            });

            input?.focus();
            input?.select();
            return;
        }

        // Only a state change the server accepted reaches here, so every re-fetch is warranted.
        hooks.doAction(HOOKS.PROMO_CHANGE, {
            promo: result.promo ?? null,
            code: result.promo?.code ?? '',
            cleared: Boolean(result.cleared),
        });
    } catch (error) {
        console.error(error);

        // A rejected *removal* must not present as an idle form. promoState.submit() throws before
        // setCode() and no Set-Cookie arrived, so the code is still live — still in the cookie,
        // still priced into the listing, still in every ticket URL — and no PROMO_CHANGE fired, so
        // nothing re-fetched. showIdle() here would also destroy the retry: it empties the input,
        // and the now-"Submit" button then hits the empty-code guard above, leaving no way to
        // re-attempt the removal short of a reload. Only this case is affected; when `removing` is
        // false the form was already idle and showIdle() is the correct no-op.
        //
        // Unless the failure came after the headers. Then the invariant above is false: the
        // response's Set-Cookie has already been applied, so the removal did land and restoring
        // "Remove" would contradict the jar. Announced as a clear, because a 200 on the removal
        // path is the only response that carries one.
        if (removing && error?.afterHeaders) {
            promoState.setCode('');
            showIdle(container);
            hooks.doAction(HOOKS.PROMO_CHANGE, {
                promo: null,
                code: '',
                cleared: true,
            });
            return;
        }

        const stillActive = removing ? activePromo.get(container) : null;
        if (stillActive) {
            showActive(container, stillActive, { error: GENERIC_ERROR });
        } else {
            showIdle(container, {
                keepValue: !removing,
                error: GENERIC_ERROR,
            });
        }
    } finally {
        pending.delete(form);
        button?.removeAttribute('aria-busy');
    }
}

/** Idempotent — ACF block previews re-run block init against the same node. */
function init(container) {
    const { form } = parts(container);
    if (!form || form.dataset.performanceInit) {
        return;
    }

    form.dataset.performanceInit = '1';
    form.addEventListener('submit', (event) => handleSubmit(event, container));
}

hooks.addAction(
    HOOKS.DATA_LOAD,
    ({ container, promo, promoInvalid, promoCode }) =>
        hydrate(container, {
            promo,
            promoInvalid,
            promoCode,
        }),
    10,
    'performance-promo-form'
);

/**
 * Keep every form on the page agreeing about the active code.
 *
 * Both blocks include templates/promo-code.twig, so two forms on one page is a designed-for case —
 * and only the block whose fetch succeeds hydrates its own form through DATA_LOAD. Remove a code in
 * one form and let the sibling block's refresh fail, and that sibling kept showing a readonly stale
 * code behind a live "Remove" button, able to start a contradictory second submission. Reconciled
 * against the state the promo module actually holds, so it is correct whether the change came from
 * a form, a URL trigger, or a block reporting the code invalid.
 */
function syncAllForms() {
    const active = activePromoState();
    document.querySelectorAll('[data-promo-form]').forEach((form) => {
        const container = form.closest('[data-performance-block]') ?? form.parentElement;
        if (!container) {
            return;
        }
        if (active) {
            showActive(container, active);
        } else {
            showIdle(container);
        }
    });
}

/**
 * The promo the page is currently priced at, or null.
 *
 * Read from the forms rather than re-fetched: a form that already shows the active code holds the
 * server's own payload (code plus message), and there is no other client-side record of it.
 */
function activePromoState() {
    const code = promoState.getCode();
    if (!code) {
        return null;
    }

    for (const form of document.querySelectorAll('[data-promo-form]')) {
        const container = form.closest('[data-performance-block]') ?? form.parentElement;
        const known = container && activePromo.get(container);
        if (known && known.code === code) {
            return known;
        }
    }
    return { code };
}

hooks.addAction(HOOKS.PROMO_CHANGE, syncAllForms, 20, 'performance-promo-form');
hooks.addAction(HOOKS.PROMO_CLEARED, syncAllForms, 20, 'performance-promo-form');

window.sitchco.performance = Object.assign(window.sitchco.performance || {}, {
    promoForm: {
        init,
        hydrate,
    },
});
